import { DEFAULT_FACING } from "../constants.ts";
import { isNight, NIGHT_SIGHT_MULTIPLIER } from "../dayNight.ts";
import {
  Classification,
  ClassificationGroup,
  classificationGroups,
  defaultClassifications,
} from "../data.ts";
import { Buff, Entity, SystemEntity } from "../types.ts";
import { absurd } from "../util/absurd.ts";
import { mergeEntityWithPrefab } from "./entity.ts";
import { getPlayer } from "./player.ts";
import { getTerrainLayers } from "../map.ts";
import { getEntitiesInRange } from "../systems/kd.ts";
import { iterateViewersInRange } from "../systems/vision.ts";
import {
  canSeeTarget,
  getMaxEntityHeight,
  getMinEntityHeight,
} from "../visibility.ts";

// Get blockers in range for visibility checks
const getBlockersInRange = (x: number, y: number, radius: number) =>
  getEntitiesInRange(x, y, radius).filter(
    (e): e is SystemEntity<"position" | "blocksLineOfSight"> =>
      !!e.blocksLineOfSight && !!e.position,
  );

const getTeam = (owner: string | undefined): "sheep" | "wolf" | "neutral" => {
  if (!owner) return "neutral";
  const player = getPlayer(owner);
  if (player?.team === "sheep") return "sheep";
  if (player?.team === "wolf") return "wolf";
  return "neutral";
};

/**
 * Check if the source entity's team can see the target entity.
 * This checks if ANY entity on the same team has LOS to the target.
 * Allies can always see each other (no LOS check needed).
 * Invisible targets require a viewer with trueVision.
 * Uses KD tree for efficient spatial lookup.
 */
export const canSee = (source: Entity, target: Entity): boolean => {
  if (!target.position) return false;
  // Allies can always see each other
  if (isAlly(source, target)) return true;

  const team = getTeam(source.owner);
  if (team === "neutral") return false;

  const terrainLayers = getTerrainLayers();
  const targetIsInvisible = isInvisible(target);

  // Get target's terrain height for early-out (use min height for tilemaps)
  const targetHeight = getMinEntityHeight(
    target.position,
    target.tilemap,
    terrainLayers,
  );

  // Iterate through nearby viewers using KD tree
  for (
    const viewer of iterateViewersInRange(
      team,
      target.position.x,
      target.position.y,
    )
  ) {
    // Invisible targets require trueVision to see
    if (targetIsInvisible && !viewer.trueVision) continue;

    // Early out: if target is higher than viewer, viewer can't see target
    const viewerHeight = getMaxEntityHeight(
      viewer.position,
      viewer.tilemap,
      terrainLayers,
    );
    if (targetHeight > viewerHeight) continue;

    if (
      canSeeTarget(
        viewer,
        { position: target.position, tilemap: target.tilemap },
        terrainLayers,
        getBlockersInRange,
      )
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Iterates over all buffs on an entity, including both direct buffs and buffs from inventory items.
 * Yields each buff for processing.
 */
export function* iterateBuffs(entity: Entity): Generator<Buff> {
  // Yield direct buffs
  if (entity.buffs) {
    for (const buff of entity.buffs) {
      yield buff;
    }
  }

  // Yield buffs from inventory items
  if (entity.inventory) {
    for (const item of entity.inventory) {
      if (item.buffs) {
        for (const buff of item.buffs) {
          yield buff;
        }
      }
    }
  }
}

export const tempUnit = (
  owner: string,
  type: string,
  x: number,
  y: number,
  extra?: Partial<Entity>,
): Entity =>
  mergeEntityWithPrefab({
    id: "",
    prefab: type,
    owner,
    position: { x, y },
    facing: DEFAULT_FACING,
    ...extra,
  }) as Entity;

export const computeUnitMovementSpeed = (unit: Entity): number => {
  const baseSpeed = unit.movementSpeed ?? 0;
  let flatSpeedBonus = 0;
  let speedMultiplier = 1.0;

  // Add flat bonuses and multipliers from buffs (including item buffs)
  for (const buff of iterateBuffs(unit)) {
    if (buff.movementSpeedBonus) {
      flatSpeedBonus += buff.movementSpeedBonus;
    }
    if (buff.movementSpeedMultiplier) {
      speedMultiplier *= buff.movementSpeedMultiplier;
    }
  }

  // Apply multipliers first, then add flat bonuses
  return baseSpeed * speedMultiplier + flatSpeedBonus;
};

/**
 * Computes the total damage a unit can deal, including base damage plus bonuses from items and buffs
 */
export const computeUnitDamage = (unit: Entity): number => {
  if (!unit.attack) return 0;

  let totalDamage = unit.attack.damage;

  // Apply multipliers first, then add bonuses from buffs (including item buffs)
  for (const buff of iterateBuffs(unit)) {
    if (buff.damageMultiplier) {
      totalDamage *= buff.damageMultiplier;
    }
  }

  for (const buff of iterateBuffs(unit)) {
    if (buff.damageBonus) {
      totalDamage += buff.damageBonus;
    }
  }

  return totalDamage;
};

/**
 * Computes the effective attack speed multiplier for a unit, including bonuses from items and buffs
 */
export const computeUnitAttackSpeed = (unit: Entity): number => {
  let speedMultiplier = 1.0;

  // Apply attack speed bonuses from buffs (including item buffs)
  for (const buff of iterateBuffs(unit)) {
    if (buff.attackSpeedMultiplier) {
      speedMultiplier *= buff.attackSpeedMultiplier;
    }
  }

  return speedMultiplier;
};

export const computeUnitSightRadius = (
  unit: Pick<Entity, "sightRadius">,
): number => {
  const base = unit.sightRadius ?? 0;
  return isNight() ? base * NIGHT_SIGHT_MULTIPLIER : base;
};

const classificationToGroup = {} as {
  [key in Classification]: ClassificationGroup;
};
for (const group in classificationGroups) {
  for (
    const classification
      of classificationGroups[group as keyof typeof classificationGroups]
  ) {
    classificationToGroup[classification] =
      group as keyof typeof classificationGroups;
  }
}

const simpleTeam = (team: Entity["team"]) => {
  if (team === "sheep") return "sheep";
  if (team === "wolf") return "wolf";
  return "neutral";
};

export const isAlly = (source: Entity | string, target: Entity | string) => {
  if (typeof target !== "string") {
    if (target.targetedAs?.includes("ally")) return true;
    if (
      target.targetedAs?.includes("enemy") ||
      target.targetedAs?.includes("neutral")
    ) return false;
  }
  const sourcePlayer = typeof source === "string" ? source : source.owner;
  const sourceTeam = simpleTeam(getPlayer(sourcePlayer)?.team);
  const targetPlayer = typeof target === "string" ? target : target.owner;
  const targetTeam = simpleTeam(getPlayer(targetPlayer)?.team);
  return sourceTeam === targetTeam && sourceTeam !== "neutral";
};

export const isEnemy = (source: Entity | string, target: Entity | string) => {
  if (typeof target !== "string") {
    if (target.targetedAs?.includes("enemy")) return true;
    if (
      target.targetedAs?.includes("ally") ||
      target.targetedAs?.includes("neutral")
    ) return false;
  }
  const sourcePlayer = typeof source === "string" ? source : source.owner;
  const sourceTeam = simpleTeam(getPlayer(sourcePlayer)?.team);
  const targetPlayer = typeof target === "string" ? target : target.owner;
  const targetTeam = simpleTeam(getPlayer(targetPlayer)?.team);
  return sourceTeam !== targetTeam && sourceTeam !== "neutral" &&
    targetTeam !== "neutral";
};

export const isNeutral = (source: Entity | string, target: Entity | string) => {
  if (typeof target !== "string") {
    if (target.targetedAs?.includes("neutral")) return true;
    if (
      target.targetedAs?.includes("ally") ||
      target.targetedAs?.includes("enemy")
    ) return false;
  }
  const sourcePlayer = typeof source === "string" ? source : source.owner;
  const sourceTeam = simpleTeam(getPlayer(sourcePlayer)?.team);
  if (sourceTeam === "neutral") return true;
  const targetPlayer = typeof target === "string" ? target : target.owner;
  const targetTeam = simpleTeam(getPlayer(targetPlayer)?.team);
  return targetTeam === "neutral";
};

const isOther = (source: Entity, target: Entity) => {
  if (target.targetedAs?.includes("other")) return true;
  if (target.targetedAs?.includes("self")) return false;
  return source !== target;
};

const isSelf = (source: Entity, target: Entity) => {
  if (target.targetedAs?.includes("self")) return true;
  if (target.targetedAs?.includes("other")) return false;
  return source === target;
};

export const isStructure = (entity: Entity) => {
  if (entity.targetedAs?.includes("structure")) return true;
  if (
    entity.targetedAs?.includes("unit") ||
    entity.targetedAs?.includes("tree") ||
    entity.targetedAs?.includes("ward")
  ) return false;
  return !!entity.tilemap && !!entity.owner;
};

export const isUnit = (entity: Entity) => {
  if (entity.type === "cosmetic") return false;
  if (entity.targetedAs?.includes("unit")) return true;
  if (
    entity.targetedAs?.includes("structure") ||
    entity.targetedAs?.includes("tree") ||
    entity.targetedAs?.includes("ward")
  ) return false;
  return !!entity.movementSpeed && !!entity.owner;
};

export const isTree = (entity: Entity) => !!entity.targetedAs?.includes("tree");

export const isWard = (entity: Entity) => {
  if (entity.targetedAs?.includes("ward")) return true;
  if (
    entity.targetedAs?.includes("unit") ||
    entity.targetedAs?.includes("tree") ||
    entity.targetedAs?.includes("structure")
  ) return false;
  return !entity.tilemap && !entity.movementSpeed && !entity.isDoodad &&
    !!entity.owner;
};

/** Checks if an entity has an invisibility buff (buildings under construction can't be invisible) */
export const isInvisible = (entity: Entity): boolean =>
  typeof entity.progress !== "number" &&
  (entity.buffs?.some((b) => b.invisible) ?? false);

/** Checks if an entity has a buff with the given name */
export const hasBuff = (entity: Entity, buffName: string): boolean =>
  entity.buffs?.some((b) => b.name === buffName) ?? false;

/** Gets the remaining duration of a buff with the given name, or undefined if not found */
export const getBuffRemainingDuration = (
  entity: Entity,
  buffName: string,
): number | undefined => {
  const buff = entity.buffs?.find((b) => b.name === buffName);
  return buff?.remainingDuration;
};

const isSpirit = (entity: Entity) => !!entity.targetedAs?.includes("spirit");

const isNotSpirit = (entity: Entity) => {
  if (entity.targetedAs?.includes("notSpirit")) return true;
  if (entity.targetedAs?.includes("spirit")) return false;
  return true;
};

const defaultClassificationsFlat = [
  Object.values(defaultClassifications).flat(),
];

export const testClassification = (
  source: Entity,
  target: Entity,
  classifications: ReadonlyArray<ReadonlyArray<Classification>> =
    defaultClassificationsFlat,
): boolean => {
  // if (!classifications?.length) return true;

  // Outer array is OR - at least one must match
  return classifications.some((andClassifications) => {
    // Inner array is AND - all must match
    const groups = andClassifications.reduce(
      (groups, c) => {
        const group = classificationToGroup[c];
        if (!groups[group]) groups[group] = [];
        groups[group].push(c as never);
        return groups;
      },
      {} as {
        [key in ClassificationGroup]:
          typeof classificationGroups[key][number][];
      },
    );

    for (const g in classificationGroups) {
      const group = g as ClassificationGroup;
      switch (group) {
        case "alliance":
          if (
            !(groups[group] ?? defaultClassifications[group]).some(
              (classification) => {
                switch (classification) {
                  case "ally":
                    return isAlly(source, target);
                  case "enemy":
                    return isEnemy(source, target);
                  case "neutral":
                    return isNeutral(source, target);
                  default:
                    throw absurd(classification);
                }
              },
            )
          ) return false;
          break;
        case "identity":
          if (
            !(groups[group] ?? defaultClassifications[group]).some(
              (classification) => {
                switch (classification) {
                  case "other":
                    return isOther(source, target);
                  case "self":
                    return isSelf(source, target);
                  default:
                    throw absurd(classification);
                }
              },
            )
          ) return false;
          break;
        case "destructibles":
          if (
            !(groups[group] ?? defaultClassifications[group]).some(
              (classification) => {
                switch (classification) {
                  case "structure":
                    return isStructure(target);
                  case "unit":
                    return isUnit(target);
                  case "tree":
                    return isTree(target);
                  case "ward":
                    return isWard(target);
                  default:
                    throw absurd(classification);
                }
              },
            )
          ) {
            return false;
          }
          break;
        case "spirit":
          if (
            !(groups[group] ?? defaultClassifications[group]).some(
              (classification) => {
                switch (classification) {
                  case "notSpirit":
                    return isNotSpirit(target);
                  case "spirit":
                    return isSpirit(target);
                  default:
                    throw absurd(classification);
                }
              },
            )
          ) return false;
          break;
        default:
          throw absurd(group);
      }
    }

    return true;
  });
};
